import { Capacitor } from '@capacitor/core';
import { Geolocation, Position } from '@capacitor/geolocation';
import { defaultsDeep } from 'es-toolkit/compat';
import { getData, LOCAL_STORAGE_KEY, saveData } from 'state/localStorage';
import { getDeviceInfo } from 'state/localStorageTyped';
import { PositionError } from 'shared/types';
import { PERMISSION_DENIED_MESSAGE } from './useUserPosition.constants';

const GEO_REQUEST_MAX_AGE = 60000; // 1 min

type PositionResult = [PositionError | null, Position | null];

type CallbackFn = (result: PositionResult) => void;

export const getCurrentPositionFromCapacitor =
  async (): Promise<PositionResult> => {
    try {
      const coords = await Geolocation.getCurrentPosition({
        maximumAge: GEO_REQUEST_MAX_AGE,
      });

      return [null, coords];
    } catch (e) {
      return [e as Error | GeolocationPositionError, null];
    }
  };

const createPermissionDeniedError = (): Error => {
  const error = new Error(PERMISSION_DENIED_MESSAGE);
  return error;
};

export const requestGeolocationPermissionsForNative =
  async (): Promise<boolean> => {
    try {
      const results = await Geolocation.requestPermissions();
      return (
        results?.location === 'granted' || results?.coarseLocation === 'granted'
      );
    } catch (e) {
      return false;
    }
  };

export const hasGeolocationPermissions = async (): Promise<boolean> => {
  try {
    const deviceInfo = getDeviceInfo();

    const results = await Geolocation.checkPermissions();

    /**
     * Safari geolocation result is always "prompt" on macOS and iOS.
     * A similar behavior is observed on iOS chrome browser.
     * To mitigate this, we check if the result is different than denied.
     * Location access is later confirm when getting the user's current
     * position with the possible geolocation error response.
     */
    if (
      deviceInfo?.isWeb &&
      ['mac', 'ios'].includes(deviceInfo?.operatingSystem ?? '')
    ) {
      return results?.location !== 'denied';
    }

    return (
      results?.location === 'granted' || results?.coarseLocation === 'granted'
    );
  } catch (e) {
    return false;
  }
};

const getCachedGeoData = (): Position | undefined =>
  getData<Position>(LOCAL_STORAGE_KEY.LAST_KNOWN_GEOLOCATION);

const persistCachedGeoData = (geoData: Position | null): void => {
  // For some reason, we can't stringify the geoData object that we get. @see https://stackoverflow.com/q/11042212/477632
  const fixedData = defaultsDeep({ coords: {} }, geoData);

  saveData(LOCAL_STORAGE_KEY.LAST_KNOWN_GEOLOCATION, fixedData);
};

const GeolocationRequestManager: {
  requests: CallbackFn[];
  hasRequestedNativePermissions: boolean;
  hasGrantedNativePermissions: boolean;
  isRequestInProgress: boolean;
  requestGeolocation: (cb: CallbackFn) => void;
  kickoffRequests: () => void;
  onError: (err: Error | GeolocationPositionError) => void;
  onSuccess: (res: Position | null) => void;
  isPermissionEnabled: () => boolean;
} = {
  requests: [],
  hasRequestedNativePermissions: false,
  hasGrantedNativePermissions: false,
  isRequestInProgress: false,

  requestGeolocation(callback: CallbackFn = (): void => {}) {
    this.requests.push(callback);

    if (!this.isRequestInProgress) {
      this.kickoffRequests();
    }
  },

  async kickoffRequests() {
    this.isRequestInProgress = true;

    if (
      Capacitor.isNativePlatform() &&
      !this.hasRequestedNativePermissions &&
      !this.hasGrantedNativePermissions
    ) {
      this.hasRequestedNativePermissions = true;
      this.hasGrantedNativePermissions =
        await requestGeolocationPermissionsForNative();

      if (!this.hasGrantedNativePermissions) {
        this.onError(createPermissionDeniedError());
        this.isRequestInProgress = false;
        return;
      }
    }

    const [error, result] = await getCurrentPositionFromCapacitor();

    if (error) {
      this.onError(error);
      this.isRequestInProgress = false;
      return;
    }

    persistCachedGeoData(result);

    this.onSuccess(result);
    this.isRequestInProgress = false;
  },

  onError(error: Error | GeolocationPositionError) {
    const cachedCoords = getCachedGeoData();

    if (cachedCoords) {
      this.onSuccess(cachedCoords);
      return;
    }

    this.requests.forEach((callback) => {
      callback([error, null]);
    });

    this.requests = [];
  },

  onSuccess(result) {
    this.requests.forEach((callback) => {
      callback([null, result]);
    });

    this.requests = [];
  },

  isPermissionEnabled() {
    return Capacitor.isNativePlatform()
      ? this.hasGrantedNativePermissions
      : true; // for web
  },
};

export const getCurrentPosition = (): Promise<PositionResult> =>
  new Promise((resolve) => {
    GeolocationRequestManager.requestGeolocation(resolve);
  });
